import { SessionStatus } from '@core/enums/session-status';

export interface IDateRange {
  start: string;
  end: string;
}

export interface MatchingSession {
  id: string;
  pin: string;
  name: string;
  team: string;
  createdAt: Date;
  closedAt: Date;
  date: Date;
  dates: IDateRange[];
  status: SessionStatus;
  mode: SessionType;
  isProject: boolean;
  fullOnboarding: boolean;
  jobMatchingEnabled?: boolean;
  location?: Location;
  image?: string;
  locale?: 'by' | 'bw';
  allowBoothData?: AllowedBoothData[];
  allowCompanyData?: AllowedCompanyData[];
  allowJobData?: AllowedJobData[];
  audience?: Audience[];
  jobDataset?: string;
  /** anymatch: the RECREWT session "Zu RECREWT übertragen" sends the exhibitors to. */
  recrewtSessionRef?: string;
  /** Brand colours of the fair, configured for the document generator (hex, `#RRGGBB`). */
  generatorConfig?: { primary?: string; secondary?: string };
}

export interface ICreateSessionRequest {
  name: string;
  mode?: SessionType;
  isProject?: boolean;
  pin?: string;
  dates: IDateRange[];
  location?: Location;
  image?: string;
  locale?: 'by' | 'bw';
  allowBoothData?: AllowedBoothData[];
  allowCompanyData?: AllowedCompanyData[];
  allowJobData?: AllowedJobData[];
  audience?: Audience[];
  jobMatchingEnabled?: boolean;
}

export interface IUpdateSessionRequest {
  name: string;
  dates: IDateRange[];
  location?: Location;
  isProject?: boolean;
  image?: string;
  locale?: 'by' | 'bw';
  allowBoothData?: AllowedBoothData[];
  allowCompanyData?: AllowedCompanyData[];
  allowJobData?: AllowedJobData[];
  audience?: Audience[];
  jobDataset?: string;
  jobMatchingEnabled?: boolean;
  recrewtSessionRef?: string;
}

export enum SessionType {
  DEFAULT,
  FAIR,
}

export const isFairSession = (s: MatchingSession): boolean => {
  return s.mode === SessionType.FAIR;
};

export const isRunningSession = (s: MatchingSession): boolean => {
  return s.status === SessionStatus.ACTIVE;
};

export const isClosedSession = (s: MatchingSession): boolean => {
  return s.status === SessionStatus.CLOSED;
};

export const isValidSessionId = (sessionId: MatchingSession['id']): boolean => {
  return sessionId.length === 24;
};

export interface Location {
  address?: string;
  region?: string;
  latitude?: number;
  location: {
    type: string;
    coordinates: [number, number];
  };
  longitude?: number;
}

export enum AllowedBoothData {
  BOOTH_NR = 'boothNr',
  DATES = 'dates',
  PROGRAM = 'program',
}

export enum AllowedCompanyData {
  BRANCH = 'branch',
  INCLUSIVE = 'inclusive',
  CUSTOM_BRANCHES = 'customBranches',
  CUSTOM_TOPICS = 'customTopics',
  STUDY_FIELDS = 'studyFields',
  URL = 'url',
  DESC = 'desc',
  QUALIFICATIONS = 'qualifications',
  ENTRY_OPPORTUNITIES = 'entryOpportunities',
  LOCATION = 'location',
  SIZE = 'size',
  CONTACT = 'contact',
  THEME = 'theme',
}

export enum AllowedJobData {
  URL = 'url',
  DESC = 'desc',
  EDUCATION_LEVEL = 'educationLevel',
  SCHOLASTIC_DATA = 'scholasticData',
  SALARY = 'estimatedSalaryEUR',
  VACATION = 'vacation',
  BENEFITS = 'benefits',
  CONTACT = 'contact',
  INTERNSHIP_POSSIBLE = 'internshipPossible',
  TOPICS = 'topics',
}

export enum Audience {
  STUDENT = 'student',
  GRADUATE = 'graduate',
  PROFESSIONAL = 'professional',
  CHANGER = 'changer',
}
